const MANAGED_UPLOAD_FILENAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,254}$/;

function sanitizeGuideImageEntry(rawImage) {
  const imageObject = rawImage && typeof rawImage === 'object' ? rawImage : { url: rawImage };
  const url = (imageObject?.url || '').toString().trim();
  if (!url) return null;
  const type = (imageObject?.type || '').toString().trim().toLowerCase();
  if (type === 'video') {
    return { ...imageObject, url, type: 'video' };
  }
  const sanitized = { ...imageObject, url };
  delete sanitized.type;
  return sanitized;
}

function normalizeComparableImageUrl(value) {
  const raw = (value || '').toString().trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, 'https://stepwisedocs.local');
    const pathname = (parsed.pathname || '').replace(/\/{2,}/g, '/');
    const search = parsed.search || '';
    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
    const prefix = isAbsolute ? parsed.origin.toLowerCase() : '';
    return `${prefix}${pathname}${search}`.toLowerCase();
  } catch (e) {
    return raw.toLowerCase();
  }
}

export function normalizeManagedUploadFilename(value) {
  const raw = (value || '').toString().trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, 'https://stepwisedocs.local');
    const baseName = (parsed.pathname || '').split('/').filter(Boolean).pop() || '';
    return MANAGED_UPLOAD_FILENAME_PATTERN.test(baseName) ? baseName : '';
  } catch (e) {
    const normalized = raw.split(/[\\/]/).filter(Boolean).pop() || '';
    return MANAGED_UPLOAD_FILENAME_PATTERN.test(normalized) ? normalized : '';
  }
}

export function deduplicateGuideImages(images) {
  const source = Array.isArray(images) ? images : [];
  const deduped = [];
  const seen = new Set();

  source.forEach((entry) => {
    const image = sanitizeGuideImageEntry(entry);
    if (!image) return;
    const key = `${image.type === 'video' ? 'video' : 'image'}:${normalizeComparableImageUrl(image.url)}`;
    if (!key || seen.has(key)) return;
    seen.add(key);
    deduped.push(image);
  });

  return deduped;
}

export function appendGuideImage(images, nextImage) {
  const image = sanitizeGuideImageEntry(nextImage);
  if (!image) return deduplicateGuideImages(images);
  return deduplicateGuideImages([...(Array.isArray(images) ? images : []), image]);
}

function matchesGuideImage(entry, oldPath, currentUrl) {
  const image = sanitizeGuideImageEntry(entry);
  if (!image || image.type === 'video') return false;

  const oldFilename = normalizeManagedUploadFilename(oldPath);
  const currentFilename = normalizeManagedUploadFilename(image.url);
  if (oldFilename && currentFilename && oldFilename === currentFilename) {
    return true;
  }

  const normalizedCurrentUrl = normalizeComparableImageUrl(currentUrl);
  if (normalizedCurrentUrl && normalizeComparableImageUrl(image.url) === normalizedCurrentUrl) {
    return true;
  }

  const normalizedOldPath = normalizeComparableImageUrl(oldPath);
  if (normalizedOldPath && normalizeComparableImageUrl(image.url) === normalizedOldPath) {
    return true;
  }

  return false;
}

export function replaceGuideImage(images, nextImage, options = {}) {
  const image = sanitizeGuideImageEntry(nextImage);
  if (!image || image.type === 'video') {
    return deduplicateGuideImages(images);
  }

  const source = Array.isArray(images) ? images : [];
  let replaced = false;
  const updated = source.map((entry) => {
    const current = sanitizeGuideImageEntry(entry);
    if (!current) return null;
    if (!matchesGuideImage(current, options.oldPath, options.currentUrl)) {
      return current;
    }
    // Repeated upload events can leave several references to the same original.
    // Keep one replacement, never a stale original alongside the edited image.
    if (replaced) return null;
    replaced = true;
    return {
      ...current,
      ...image,
      url: image.url
    };
  }).filter(Boolean);

  if (!replaced) {
    const nonVideoIndexes = updated.reduce((acc, entry, index) => {
      if (entry?.type !== 'video') acc.push(index);
      return acc;
    }, []);
    if (nonVideoIndexes.length === 1) {
      const targetIndex = nonVideoIndexes[0];
      updated[targetIndex] = {
        ...updated[targetIndex],
        ...image,
        url: image.url
      };
      replaced = true;
    }
  }

  return deduplicateGuideImages(updated);
}
